import { useState } from "react";
import {
  Button,
  Input,
  Label,
  ListBox,
  Select,
  TextField,
} from "@heroui/react";
import { Pencil, X, Check } from "lucide-react";

import { UserRole } from "@/features/users/type.ts";
import { useCities } from "@/features/city/use-cities.ts";
import {
  useUserCityUpdate,
  useUserNameUpdate,
  useUserRoleUpdate,
} from "@/features/users/use-user.ts";
import { userTools } from "@/shared/helper/user.ts";

export const UserCityEdit = ({
  userId,
  cityId,
}: {
  userId: string;
  cityId: number | null;
}) => {
  const [isEdit, setIsEdit] = useState<boolean>(!cityId);
  const cities = useCities();

  const mutation = useUserCityUpdate({
    onSuccess: () => {
      setIsEdit(false);
    },
  });

  const city = cities.data.data.find((item) => item.id === cityId);

  if (!isEdit) {
    return (
      <div className="group flex flex-row gap-2 items-center cursor-pointer">
        {city?.name ?? cityId}

        <div className="group-hover:block hidden">
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            onPress={() => setIsEdit(true)}
          >
            <Pencil size={16} />
          </Button>
        </div>
      </div>
    );
  }

  const cityItems = cities.data.data.map((item) => ({
    key: `${item.id}`,
    title: item.name,
  }));

  return (
    <div className="flex flex-row gap-2 items-center h-10">
      <Select
        isDisabled={mutation.isPending}
        selectedKey={cityId ? `${cityId}` : null}
        onSelectionChange={(key) => {
          if (key) {
            mutation.mutate({ userId, cityId: Number(key) });
          }
        }}
      >
        <Label>Город</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox items={cityItems}>
            {({ key, title }) => (
              <ListBox.Item id={key} textValue={title}>
                {title}
              </ListBox.Item>
            )}
          </ListBox>
        </Select.Popover>
      </Select>

      {cityId && (
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          onPress={() => setIsEdit(false)}
        >
          <X size={16} />
        </Button>
      )}
    </div>
  );
};

export const UserRoleEdit = ({
  userId,
  role,
}: {
  userId: string;
  role: UserRole;
}) => {
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const mutation = useUserRoleUpdate({
    onSuccess: () => {
      setIsEdit(false);
    },
  });

  if (!isEdit) {
    return (
      <div className="group flex flex-row gap-2 items-center cursor-pointer">
        {userTools.role.toString(role)}

        <div className="group-hover:block hidden">
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            onPress={() => setIsEdit(true)}
          >
            <Pencil size={16} />
          </Button>
        </div>
      </div>
    );
  }

  const roleItems = userTools.role.list.map((value) => ({
    key: value,
    title: userTools.role.toString(value),
  }));

  return (
    <div className="flex flex-row gap-2 items-center h-10">
      <Select
        isDisabled={mutation.isPending}
        selectedKey={role}
        onSelectionChange={(key) => {
          if (key) {
            mutation.mutate({ userId, role: key as UserRole });
          }
        }}
      >
        <Label>Роль</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox items={roleItems}>
            {({ key, title }) => (
              <ListBox.Item id={key} textValue={title}>
                {title}
              </ListBox.Item>
            )}
          </ListBox>
        </Select.Popover>
      </Select>

      <Button
        isIconOnly
        size="sm"
        variant="ghost"
        onPress={() => setIsEdit(false)}
      >
        <X size={16} />
      </Button>
    </div>
  );
};

export const UserNameEdit = ({
  userId,
  name,
}: {
  userId: string;
  name?: string;
}) => {
  const [isEdit, setIsEdit] = useState<boolean>(!name);
  const [value, setValue] = useState<string>(name ?? "");

  const mutation = useUserNameUpdate({});

  if (!isEdit) {
    return (
      <div className="group flex flex-row gap-2 items-center cursor-pointer max-w-64">
        {value || name}

        <div className="group-hover:block hidden">
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            onPress={() => setIsEdit(true)}
          >
            <Pencil size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row gap-2 items-center h-10 max-w-64">
      <TextField>
        <Input value={value} onChange={(e) => setValue(e.target.value)} />
      </TextField>

      {name && (
        <Button
          isIconOnly
          isDisabled={!value}
          size="sm"
          variant="outline"
          onPress={() => {
            mutation.mutate({ userId, name: value });
            setIsEdit(false);
          }}
        >
          <Check size={16} />
        </Button>
      )}

      {name && (
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          onPress={() => setIsEdit(false)}
        >
          <X size={16} />
        </Button>
      )}
    </div>
  );
};
